import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserPlus, Loader2, Phone, MapPin, Heart } from "lucide-react";
import { useCreateManualUser } from "@/hooks/admin/useCreateManualUser";

interface AddManualUserDialogProps {
  gymId: string;
  onUserAdded?: () => void;
}

export const AddManualUserDialog = ({ gymId, onUserAdded }: AddManualUserDialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dni, setDni] = useState("");
  const [role, setRole] = useState("socio");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [emergencyPhone, setEmergencyPhone] = useState("");
  const [healthInsurance, setHealthInsurance] = useState("");

  const { mutate: createUser, isPending } = useCreateManualUser();

  const resetForm = () => {
    setFirstName("");
    setLastName("");
    setDni("");
    setRole("socio");
    setPhone("");
    setAddress("");
    setEmergencyPhone("");
    setHealthInsurance("");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!firstName.trim() || !dni.trim()) return;

    createUser(
      {
        firstName: firstName.trim(),
        lastName: lastName.trim() || undefined,
        dni: dni.trim(),
        role,
        gymId,
        phone: phone.trim() || undefined,
        address: address.trim() || undefined,
        emergencyPhone: emergencyPhone.trim() || undefined,
        healthInsurance: healthInsurance.trim() || undefined,
      },
      {
        onSuccess: () => {
          setIsOpen(false);
          resetForm();
          onUserAdded?.();
        },
      }
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="default" size="sm" className="gap-2">
          <UserPlus className="h-4 w-4" />
          Agregar Usuario
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] max-h-[80vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle>Agregar Usuario Manual</DialogTitle>
          <DialogDescription>
            Agrega un usuario que aún no se ha registrado. Cuando se registre con el mismo documento, sus datos se unificarán automáticamente.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col overflow-hidden">
          <div className="grid gap-4 py-4 overflow-y-auto flex-1 pr-1">
            <div className="grid gap-2">
              <Label htmlFor="firstName">Nombre *</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Ingresa el nombre"
                required
                disabled={isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="lastName">Apellido</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Ingresa el apellido (opcional)"
                disabled={isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="dni">Documento (DNI) *</Label>
              <Input
                id="dni"
                value={dni}
                onChange={(e) => setDni(e.target.value)}
                placeholder="Ingresa el documento"
                required
                disabled={isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="role">Rol</Label>
              <Select value={role} onValueChange={setRole} disabled={isPending}>
                <SelectTrigger id="role">
                  <SelectValue placeholder="Selecciona un rol" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="socio">Socio</SelectItem>
                  <SelectItem value="profesor">Profesor</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Optional fields */}
            <div className="grid gap-2">
              <Label htmlFor="phone" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Teléfono <span className="text-muted-foreground text-xs">(opcional)</span>
              </Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Ingresa el teléfono"
                disabled={isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="address" className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
                Dirección <span className="text-muted-foreground text-xs">(opcional)</span>
              </Label>
              <Input
                id="address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder="Ingresa la dirección"
                disabled={isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="emergencyPhone" className="flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                Tel. Emergencia <span className="text-muted-foreground text-xs">(opcional)</span>
              </Label>
              <Input
                id="emergencyPhone"
                value={emergencyPhone}
                onChange={(e) => setEmergencyPhone(e.target.value)}
                placeholder="Ingresa teléfono de emergencia"
                disabled={isPending}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="healthInsurance" className="flex items-center gap-1.5">
                <Heart className="h-3.5 w-3.5 text-muted-foreground" />
                Obra Social <span className="text-muted-foreground text-xs">(opcional)</span>
              </Label>
              <Input
                id="healthInsurance"
                value={healthInsurance}
                onChange={(e) => setHealthInsurance(e.target.value)}
                placeholder="Ingresa la obra social"
                disabled={isPending}
              />
            </div>
          </div>
          <DialogFooter className="pt-4 border-t border-border sticky bottom-0 bg-background">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsOpen(false)}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending || !firstName.trim() || !dni.trim()}>
              {isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Agregando...
                </>
              ) : (
                "Agregar Usuario"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
